import React, { useState, useEffect } from 'react';
import { Clock, Trash2, ChevronDown, ChevronUp, RefreshCw, RotateCcw } from 'lucide-react';
import { format, parseISO } from 'date-fns';

interface HistoryEntry {
  id: string;
  tool_id: string;
  tool_name: string;
  created_at: string;
  input_summary: string;
  output_preview: string;
  full_output: string;
  snapshot?: string | null; // only present on single-entry fetch
}

interface HistorySectionProps {
  toolId: string;
  onUseHistory?: (entry: HistoryEntry) => void;
}

const HistorySection: React.FC<HistorySectionProps> = ({ toolId, onUseHistory }) => {
  const [entries, setEntries] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/history?limit=50', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load history');
      const json = await res.json();
      // Filter by current tool
      const filtered = json.data.filter((e: HistoryEntry) => e.tool_id === toolId);
      setEntries(filtered);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [toolId]);

  const handleDelete = async (id: string) => {
    try {
      const res = await fetch(`/api/history/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Delete failed');
      setEntries((prev) => prev.filter((e) => e.id !== id));
      if (expandedId === id) setExpandedId(null);
    } catch {
      // Keep entry on failure
    } finally {
      setDeleteConfirmId(null);
    }
  };

  // Fetch full entry (with snapshot) then call onUseHistory
  const handleUse = async (entry: HistoryEntry) => {
    if (!onUseHistory) return;
    setRestoringId(entry.id);
    try {
      const res = await fetch(`/api/history/${entry.id}`, { credentials: 'include' });
      if (res.ok) {
        const full: HistoryEntry = await res.json();
        onUseHistory(full);
      } else {
        // Fallback: pass list version (no snapshot)
        onUseHistory(entry);
      }
    } catch {
      onUseHistory(entry);
    } finally {
      setRestoringId(null);
    }
  };

  const formatDate = (iso: string) => {
    try {
      return format(parseISO(iso), 'dd MMM yyyy, HH:mm');
    } catch {
      return iso;
    }
  };

  if (loading) {
    return (
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="flex items-center gap-2 mb-4">
          <Clock size={18} className="text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-800">History</h3>
        </div>
        <div className="text-sm text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="flex items-center gap-2 mb-4">
          <Clock size={18} className="text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-800">History</h3>
        </div>
        <div className="text-sm text-red-600 flex items-center gap-2">
          {error}
          <button onClick={fetchHistory} className="ml-2 text-blue-600 hover:text-blue-700 underline">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="flex items-center gap-2 mb-4">
          <Clock size={18} className="text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-800">History</h3>
        </div>
        <p className="text-sm text-gray-500">No history yet for this tool.</p>
      </div>
    );
  }

  return (
    <div className="mt-8 pt-6 border-t border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Clock size={18} className="text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-800">History</h3>
          <span className="text-xs text-gray-400">({entries.length})</span>
        </div>
        <button
          onClick={fetchHistory}
          className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
        >
          <RefreshCw size={12} />
          Refresh
        </button>
      </div>

      <div className="space-y-2">
        {entries.map((entry) => (
          <div
            key={entry.id}
            className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-sm transition-shadow"
          >
            {deleteConfirmId === entry.id ? (
              <div className="px-4 py-3 flex items-center justify-between gap-3 bg-red-50">
                <p className="text-sm text-gray-700">Delete this entry?</p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setDeleteConfirmId(null)}
                    className="px-3 py-1 rounded-lg text-xs text-gray-600 hover:bg-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDelete(entry.id)}
                    className="px-3 py-1 rounded-lg text-xs font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="px-4 py-3 flex items-start justify-between gap-3">
                  <button
                    onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                    className="flex-1 text-left min-w-0"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-xs text-gray-500">{formatDate(entry.created_at)}</p>
                    </div>
                    {entry.input_summary && (
                      <p className="text-xs text-gray-400 mb-1 truncate">{entry.input_summary}</p>
                    )}
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {entry.output_preview.substring(0, 120)}
                    </p>
                  </button>
                  <div className="flex items-center gap-1 shrink-0">
                    {onUseHistory && (
                      <button
                        onClick={() => handleUse(entry)}
                        disabled={restoringId === entry.id}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg transition-colors disabled:opacity-60"
                        style={{
                          background: 'rgba(84,196,182,0.12)',
                          color: '#216974',
                          border: '1px solid rgba(84,196,182,0.25)',
                        }}
                        title="Restore this result"
                      >
                        <RotateCcw size={11} />
                        {restoringId === entry.id ? 'Loading…' : 'Restore'}
                      </button>
                    )}
                    <button
                      onClick={() => setDeleteConfirmId(entry.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                    <button
                      onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded transition-colors"
                    >
                      {expandedId === entry.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                  </div>
                </div>

                {expandedId === entry.id && (
                  <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 space-y-3">
                    {entry.input_summary && (
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Input</p>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{entry.input_summary}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Preview</p>
                      <div className="p-3 bg-white rounded-xl border border-gray-200">
                        <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                          {entry.output_preview}
                        </p>
                      </div>
                    </div>
                    {onUseHistory && (
                      <button
                        onClick={() => handleUse(entry)}
                        disabled={restoringId === entry.id}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-60"
                        style={{
                          background: 'linear-gradient(135deg, #54c4b6 0%, #216974 100%)',
                          color: '#fff',
                        }}
                      >
                        <RotateCcw size={14} />
                        {restoringId === entry.id ? 'Loading…' : 'Restore This Result'}
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default HistorySection;
